import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { usersApi } from '../services/api';
import { colors } from '../theme/tokens';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accountInfo, setAccountInfo] = useState<any>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      try {
        setLoading(true);
        
        // Refresh account data to show updated balance
        const accountResponse = await usersApi.getUserAccountInfo();
        setAccountInfo(accountResponse.data);
        
        // You could also verify the payment with your backend here
        // const verificationResponse = await paymentsApi.verifyPayment(sessionId);
        
      } catch (err) {
        console.error('Error handling payment success:', err);
        setError('There was an issue processing your payment. Please contact support if your payment was deducted.');
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) {
      handlePaymentSuccess();
    } else {
      setError('Invalid payment session. Please contact support.');
      setLoading(false);
    }
  }, [sessionId]);

  const handleGoToAccount = () => {
    navigate('/user/account?payment_success=true');
  };

  const handleGoToDashboard = () => {
    navigate('/user/');
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        flexDirection="column"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Processing your payment...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        flexDirection="column"
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleGoToAccount}>
          Go to Account
        </Button>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="400px"
      padding={3}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon 
            color="success" 
            sx={{ fontSize: 80, mb: 2 }} 
          />
          
          <Typography variant="h4" gutterBottom color="success.main">
            Payment Successful!
          </Typography>
          
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Thank you for your purchase. Your credits have been added to your account.
          </Typography>

          {accountInfo && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: colors.successTintBg,
                border: `1px solid ${colors.successTintBorder}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" color="success.main">
                Current Balance: {accountInfo.currentBalance} credits
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            You will receive a confirmation email shortly. You can now use your credits for transcription and bookmark generation services.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              onClick={handleGoToAccount}
              size="large"
            >
              View Account
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleGoToDashboard}
              size="large"
            >
              Go to Dashboard
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentSuccess;
